/**
 * Detects whether a string is already valid HTML.
 */
function isHTML(str: string): boolean {
  return /<[a-z][\s\S]*>/i.test(str);
}

/**
 * Sanitizes an individual line to prevent XSS before injecting into HTML.
 */
function escapeLine(line: string): string {
  return line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

/**
 * Converts a legacy plain-text string (with \n line breaks) into
 * clean, semantic HTML for use inside a Jodit WYSIWYG editor.
 *
 * Rules:
 *  - If already HTML → return as-is (no double processing)
 *  - Blank lines       → paragraph boundary (<p> separator)
 *  - Single \n         → soft line break (<br>) within a paragraph
 *  - Empty input       → returns empty string
 */
export function convertLegacyTextToHtml(raw: string | null | undefined): string {
  if (!raw || raw.trim() === '') return '';

  // Already HTML — Jodit can handle it natively, skip transformation
  // istanbul ignore next
  if (isHTML(raw)) return raw;

  const paragraphs = raw
    .split(/\n{2,}/) // Step 1: Split on blank lines → paragraph blocks
    .map((block) => block.trim()) // Step 2: Trim each block
    .filter((block) => block.length > 0) // Step 3: Remove empty blocks
    .map((block) => {
      const inner = block
        .split('\n') // Step 4: Split single newlines within block
        .map((line) => line.trim()) // Step 5: Trim each line
        .filter((line) => line.length > 0) // Step 6: Remove blank lines within block
        .map(escapeLine) // Step 7: Sanitize against XSS
        .join('<br>'); // Step 8: Single \n becomes <br>

      return `<p>${inner}</p>`; // Step 9: Wrap block in semantic <p>
    });

  return paragraphs.join(''); // Step 10: Join all paragraphs
}
